import React, { useState } from 'react';
import { Button, InputText } from 'design-system/Form';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import { Modal, ModalHeader, ModalBody } from 'react-scale/components/design-system/Modal';
import { makeStyles } from 'react-scale/components/design-system/styles';
import { TaskType } from '@scale/shared-types';
import { Switch } from 'react-scale/components/design-system';
import { textContainsCodenames, extractCodenamesFromText } from '@scale/shared/codenames';
import { CodenamesWarningModal } from 'react-scale/corp/components/CodenamesWarningModal';
import { useGetRandomConfig } from 'react-scale/corp/views/CorpProjectGroupManager/communication/apiClient';

// NOTE: this was all copied, pasted, and refactored from QMUpdatePermissionGroupModal.tsx

const QMUpdateDescriptionModal: React.FC<{
  projectId: string;
  isModalOpen: boolean;
  setModalOpen: (isModalOpen: boolean) => void;
  taskType?: TaskType;
  projectDescription: string;
  setProjectDescription: (newDescription: string) => void;
  isAttemptCompare: boolean;
  setIsAttemptCompare: (isAttemptCompare: boolean) => void;
}> = ({
  projectId,
  isModalOpen,
  setModalOpen,
  taskType,
  projectDescription,
  setProjectDescription,
  isAttemptCompare,
  setIsAttemptCompare,
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState<boolean>(false);
  const [openWarningModal, setOpenWarningModal] = useState<boolean>(false);
  const [codenameUsed, setCodenameUsed] = useState<string>('');
  const { data: customerCodenamesConfig } = useGetRandomConfig({
    randomConfigName: 'customer-codenames-list',
  });
  const customerCodenames = React.useMemo(
    () => customerCodenamesConfig?.value ?? [],
    [customerCodenamesConfig],
  );

  const updateNewDescription = async () => {
    const inputContainsCodenames = textContainsCodenames(projectDescription, customerCodenames);
    if (inputContainsCodenames) {
      const codenamesInDescription = extractCodenamesFromText(
        projectDescription,
        customerCodenames,
      );
      setOpenWarningModal(true);
      setCodenameUsed(codenamesInDescription.join(', '));
      return;
    }
    setLoading(true);
    try {
      await apiFetch(null, `corp-api/update_project_description`, {
        method: 'POST',
        body: {
          description: projectDescription,
          project: projectId,
          isAttemptCompare,
        },
      });
      setModalOpen(false);
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CodenamesWarningModal
        open={openWarningModal}
        codename={codenameUsed}
        onClose={() => setOpenWarningModal(false)}
      />
      <Modal isOpen={isModalOpen && !openWarningModal} onRequestClose={() => setModalOpen(false)}>
        <ModalHeader onRequestClose={() => setModalOpen(false)}>
          Update Project Description
        </ModalHeader>
        <ModalBody>
          <InputText
            value={projectDescription}
            placeholder={projectDescription}
            onChange={e => setProjectDescription(e.target.value)}
          />
          {taskType === TaskType.Chat && (
            <div className={classes.marginTop}>
              <Switch
                label="Is Attempt Compare"
                checked={isAttemptCompare}
                onChange={() => setIsAttemptCompare(!isAttemptCompare)}
              />
              <p>
                Attempt Compare projects include 1. response writing or rewriting during attempt,
                and 2. side-x-side comparison against SOTA model during review.{' '}
                <a href="https://scale.atlassian.net/wiki/spaces/OP/pages/474120212/AttemptCompare+Pipeline">
                  See Docs
                </a>
              </p>
            </div>
          )}
          <br />
          <Button
            primary
            type="button"
            disabled={loading}
            className={classes.button}
            onClick={updateNewDescription}
          >
            {loading ? 'Loading...' : 'Save'}
          </Button>
          <br />
        </ModalBody>
      </Modal>
    </>
  );
};

const useStyles = makeStyles({
  button: {
    marginLeft: 10,
    float: 'right',
  },
  marginTop: {
    marginTop: 24,
  },
});

export default QMUpdateDescriptionModal;
